import { ChangeDetectionStrategy, Component, Input, Self, ChangeDetectorRef, input } from '@angular/core';
import { NgControl } from '@angular/forms';
import { BazisFileService } from '@bazis/form/services/file.service';
import { FILE_EXTENSIONS, FileType } from '@bazis/form/models/form-element.types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, tap } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { BazisToastService } from '@bazis/shared/services/toast.service';
import { BazisConfigurationService, MAX_FILE_SIZE } from '@bazis/configuration.service';
import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';

@UntilDestroy()
@Component({
    selector: 'bazis-input-file-button',
    templateUrl: './input-file-button.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BazisInputFileButtonComponent extends BazisControlValueAccessor {
    // макс. кол-во файлов
    @Input() max: number = 1;

    // текст кнопки
    @Input() buttonKey: string = 'form.title.inputFile.addFiles';

    // параметры для кнопки
    @Input() buttonParams: any = null;

    @Input() fileType: FileType = 'any';

    @Input() directExtentions: string[] = null;

    @Input() fillButton: string = 'outline';

    @Input() colorButton: string = 'action';

    // Temp, depricated.
    @Input() iconKey: string = '';

    readonly iconStart = input<string>();

    readonly iconEnd = input<string>();

    readonly icon = input<string>();

    @Input() maxFileSize = MAX_FILE_SIZE;

    files: { id: string; file: File }[] = [];

    extentions = FILE_EXTENSIONS[this.fileType];

    isDisabled: boolean = false;

    protected uploadingDict: { [key: string]: any } = {};

    _isInited = false;

    displayAllowedExtentions: string[];

    constructor(
        @Self() public ngControl: NgControl,
        protected cdr: ChangeDetectorRef,
        public configurationService: BazisConfigurationService,
        private fileService: BazisFileService,
        private toastService: BazisToastService,
    ) {
        super(ngControl, cdr, configurationService);
        ngControl.valueAccessor = this;
    }

    extendOnInit() {
        this.displayAllowedExtentions = this.directExtentions || FILE_EXTENSIONS[this.fileType];
        this._isInited = true;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    fileChanged(id: string, e) {
        const element = e.currentTarget as HTMLInputElement;
        let fileList: FileList | null = element.files;

        if (fileList.length === 1) {
            const file = fileList[0];

            this.preUploadFile(file, element, id);
            this.uploadFile(file, id);
        }
    }

    preUploadFile(file: File, element: HTMLInputElement, id: string) {
        // this.ngControl.control.setErrors(null);

        const result = this.fileService.preUploadFile(
            file,
            element,
            this.directExtentions || FILE_EXTENSIONS[this.fileType],
            this.maxFileSize,
        );

        if (result.error) {
            // this.ngControl.control.setErrors({ [result.error]: true });
            this.removeFile(this.files.findIndex((v) => v.id === id));
            return;
        }

        this.uploadingDict[id] = {
            id,
            file,
            name: file.name,
            size: file.size,
            extension: result.extension,
            status: 'progress',
            progress: 0,
            isImage: FILE_EXTENSIONS.image.indexOf(result.extension) > -1,
        };
    }

    checkRealMimeType(file: File, header: string) {
        return this.fileService.checkRealMimeType(
            file,
            header,
            this.directExtentions || FILE_EXTENSIONS[this.fileType],
        );
    }

    uploadFile(file: File, id: string) {
        this.fileService
            .readFile$(file, this.directExtentions || FILE_EXTENSIONS[this.fileType])
            .pipe(
                tap((r) => {
                    if (!this.uploadingDict[id]) return;
                    delete this.uploadingDict[id];
                    this.updateFormControl();
                }),
                catchError((e) => {
                    if (e?.error === 'mimeTypeError') {
                        // this.ngControl.control.setErrors({ fileErrorMime: true });
                        this.removeFile(this.files.findIndex((v) => v.id === id));
                    }
                    return EMPTY;
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    removeFile(index) {
        const fileId = this.files[index].id;
        this.files.splice(index, 1);
        if (this.uploadingDict[fileId]) {
            delete this.uploadingDict[fileId];
        } else {
            this.updateFormControl();
        }
        this.markAsTouched();
    }

    addFiles(e) {
        const element = e.currentTarget as HTMLInputElement;
        let fileList: FileList | null = element.files;

        for (let index = 0; index < fileList.length; index++) {
            const file = fileList[index];
            const id = `${uuidv4()}`;
            if (this.files.length < this.max) {
                this.uploadingDict[id] = { status: 'empty' };
                this.files.push({ id, file });
            }
            this.preUploadFile(file, element, id);
            if (!this.uploadingDict[id]) return;

            this.uploadFile(file, id);
        }
        this.markAsTouched();
        element.value = '';
    }

    updateFormControl() {
        const values = this.files.filter((v) => v && !this.uploadingDict[v.id]).map((v) => v.file);
        if (values.length !== this.files.length) return;
        this.onChange(this.max > 1 ? values : values.length ? values[0] : null);
        this.files = [];
    }
}
